import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';

import { AuthService } from '../../api-authorization/authorize.service';
import { SpsImportApiService } from '../sps-import-api.service';
import { SpsPruefungApiService } from '../sps-pruefung-api.service';
import { NetworkStatusService, TimeoutFehler } from './network-status.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineSyncService } from './offline-sync.service';
import {
  anlagenteilOptionenDerListe,
  anlagenteilOptionenDerPruefung,
  filtereEintraege,
  filtereSignale,
  fortschrittVon,
  nachbarnVon,
  seiteVon,
  sortiereEintraege,
  sortiereSignale
} from './sps-offline-filter';

import { neueUuid } from '../../Models/messstelle.model';
import {
  SPS_FILTER_STANDARD_GROESSE,
  SPS_LISTEN_STANDARD_GROESSE,
  SpsAnlagenteilOptionen,
  SpsFortschritt,
  SpsImportEintrag,
  SpsImportListe,
  SpsImportListeDetail,
  SpsListenFilter,
  SpsPruefprotokoll,
  SpsPruefstatusRequest,
  SpsSeite,
  SpsSignal,
  SpsSignalDetail,
  SpsSignalFilter,
  SpsSignalNachbarn,
  leererSpsListenFilter
} from '../../Models/Sps';
import {
  DatenErgebnis,
  OfflineSpsSignalEintrag,
  SchreibErgebnis,
  SyncQueueEintrag
} from '../../Models/offline.model';

/** Woher das Projekt einer Anfrage stammt – direkt bekannt oder erst aus dem lokalen Bestand zu ermitteln. */
type ProjektQuelle = number | null | (() => Promise<number | null>);

/**
 * Der Zugang der SPS-Komponenten zu ihren Daten – nach demselben Muster wie
 * `InbetriebnahmeDataService` für die Messstellen.
 *
 * Lesen:  Server versuchen → bei Erfolg anzeigen (und den lokalen Bestand
 *         nachziehen, wenn das Projekt offline gehalten wird); bei
 *         Netzwerkfehler oder Zeitüberschreitung aus der IndexedDB. Filter,
 *         Sortierung, Seiten, Fortschritt und Anlagenteil-Optionen werden
 *         dann LOKAL gerechnet, mit denselben Regeln wie serverseitig.
 *
 * Schreiben: nur der Prüfstatus samt Bemerkung. Server versuchen → bei
 *         Netzwerkfehler lokal speichern und in die Warteschlange. Alles
 *         andere (Zuordnung klären, Löschen, Import, Listenupdate,
 *         Bereinigung) bleibt bewusst online.
 *
 * Der Online-Weg ist ein reiner Observable-Strom: die Serverantwort kommt
 * ohne Umweg über den lokalen Speicher bei der Komponente an. Erst ein
 * Netzwerkfehler führt in die IndexedDB.
 *
 * Was NICHT zum Offline-Fallback führt: 400, 401, 403, 404, 409. Das sind
 * echte Antworten – ein Versionskonflikt (409) wird der Komponente wie bisher
 * gemeldet, damit sie die Zeile neu lädt.
 */
@Injectable({ providedIn: 'root' })
export class SpsDataService {

  private static readonly LESE_TIMEOUT_MS = 12_000;
  private static readonly SCHREIB_TIMEOUT_MS = 15_000;

  constructor(
    private pruefungApi: SpsPruefungApiService,
    private importApi: SpsImportApiService,
    private db: OfflineDbService,
    private network: NetworkStatusService,
    private sync: OfflineSyncService,
    private auth: AuthService
  ) { }

  // ===================================================================
  // LESEN: LISTEN
  // ===================================================================

  getListen(projektId: number): Observable<DatenErgebnis<SpsImportListe[]>> {
    return this.lies(
      () => this.importApi.getListen(projektId),
      projektId,
      async () => (await this.db.ladeSpsListen(projektId))
        .map(l => l.data as SpsImportListe)
        .sort((a, b) => a.name.localeCompare(b.name, 'de'))
    );
  }

  getListe(listeId: number): Observable<DatenErgebnis<SpsImportListeDetail>> {
    return this.lies(
      () => this.importApi.getListe(listeId),
      () => this.projektVonListe(listeId),
      async () => {
        const lokal = await this.db.ladeSpsListe(listeId);

        if (!lokal) {
          throw new Error(
            'Diese SPS-Liste steht offline nicht zur Verfügung. '
            + 'Das Projekt muss vorher offline verfügbar gemacht werden.'
          );
        }

        return lokal.data;
      }
    );
  }

  getEintraege(listeId: number, filter: Partial<SpsListenFilter> | null): Observable<DatenErgebnis<SpsSeite<SpsImportEintrag>>> {
    const voll: SpsListenFilter = { ...leererSpsListenFilter(), ...(filter ?? {}) };

    return this.lies(
      () => this.importApi.getEintraege(listeId, voll),
      () => this.projektVonListe(listeId),
      async () => {
        const eintraege = (await this.db.ladeSpsEintraege(listeId)).map(e => e.data);
        const gefiltert = sortiereEintraege(filtereEintraege(eintraege, voll), voll.sortierung);

        return seiteVon(gefiltert, voll.seite, voll.groesse, SPS_LISTEN_STANDARD_GROESSE);
      }
    );
  }

  getListenAnlagenteile(listeId: number): Observable<DatenErgebnis<SpsAnlagenteilOptionen>> {
    return this.lies(
      () => this.importApi.getAnlagenteile(listeId),
      () => this.projektVonListe(listeId),
      async () => anlagenteilOptionenDerListe(
        (await this.db.ladeSpsEintraege(listeId)).map(e => e.data))
    );
  }

  // ===================================================================
  // LESEN: SIGNALE
  // ===================================================================

  getSignale(filter: SpsSignalFilter): Observable<DatenErgebnis<SpsSeite<SpsSignal>>> {
    return this.lies(
      () => this.pruefungApi.getSignale(filter),
      filter.projektId,
      async () => {
        const signale = (await this.db.ladeSpsSignale(filter.projektId)).map(s => s.data);
        const gefiltert = sortiereSignale(filtereSignale(signale, filter), filter.sortierung);

        return seiteVon<SpsSignal>(gefiltert, filter.seite, filter.groesse, SPS_FILTER_STANDARD_GROESSE);
      },
      seite => this.aktualisiereLokaleSignale(seite.eintraege)
    );
  }

  getSignal(signalId: number): Observable<DatenErgebnis<SpsSignalDetail>> {
    return this.lies(
      () => this.pruefungApi.getSignal(signalId),
      () => this.projektVonSignal(signalId),
      async () => {
        const lokal = await this.db.ladeSpsSignal(signalId);

        if (!lokal) {
          throw new Error(
            'Dieses Signal steht offline nicht zur Verfügung. '
            + 'Das Projekt muss vorher offline verfügbar gemacht werden.'
          );
        }

        return lokal.data;
      },
      detail => this.aktualisiereLokaleSignale([detail])
    );
  }

  getVerlauf(signalId: number): Observable<DatenErgebnis<SpsPruefprotokoll[]>> {
    return this.lies(
      () => this.pruefungApi.getVerlauf(signalId),
      () => this.projektVonSignal(signalId),
      // Online kommt der Verlauf älteste zuerst – lokal liegt er neueste zuerst.
      async () => [...((await this.db.ladeSpsSignal(signalId))?.data.protokolle ?? [])].reverse()
    );
  }

  getNachbarn(signalId: number, filter: SpsSignalFilter): Observable<DatenErgebnis<SpsSignalNachbarn>> {
    return this.lies(
      () => this.pruefungApi.getNachbarn(signalId, filter),
      filter.projektId,
      async () => {
        const signale = (await this.db.ladeSpsSignale(filter.projektId)).map(s => s.data);
        return nachbarnVon(sortiereSignale(filtereSignale(signale, filter), filter.sortierung), signalId);
      }
    );
  }

  getFortschritt(projektId: number): Observable<DatenErgebnis<SpsFortschritt>> {
    return this.lies(
      () => this.pruefungApi.getFortschritt(projektId),
      projektId,
      async () => fortschrittVon((await this.db.ladeSpsSignale(projektId)).map(s => s.data))
    );
  }

  getPruefungAnlagenteile(projektId: number): Observable<DatenErgebnis<SpsAnlagenteilOptionen>> {
    return this.lies(
      () => this.pruefungApi.getAnlagenteile(projektId),
      projektId,
      async () => anlagenteilOptionenDerPruefung((await this.db.ladeSpsSignale(projektId)).map(s => s.data))
    );
  }

  // ===================================================================
  // SCHREIBEN: PRÜFSTATUS
  // ===================================================================

  /**
   * Prüfstatus und Bemerkung eines Signals.
   *
   * Online wie bisher (409 bei Versionskonflikt wird durchgereicht). Ohne
   * Verbindung wird lokal gespeichert: das Signal zeigt sofort den neuen
   * Stand, ein lokaler Verlaufseintrag entsteht, und die Änderung steht mit
   * dem Änderungsstand des Downloads als Basis in der Warteschlange.
   */
  setStatus(signal: SpsSignal, request: SpsPruefstatusRequest): Observable<SchreibErgebnis<SpsSignal>> {
    if (!this.network.lohntVersuch()) {
      return from(this.lokalSchreiben(signal, request));
    }

    return this.pruefungApi.setStatus(signal.id, request).pipe(
      this.mitTimeout(SpsDataService.SCHREIB_TIMEOUT_MS),
      tap(gespeichert => {
        this.network.meldeErfolg();

        // Nebenwirkung, nicht Teil des Vorgangs: die lokale Kopie nachziehen.
        void this.aktualisiereLokaleSignale([gespeichert], true);
      }),
      map(gespeichert => ({ daten: gespeichert, lokal: false })),
      catchError(fehler => {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          return throwError(() => fehler);
        }

        return from(this.lokalSchreiben(signal, request));
      })
    );
  }

  private async lokalSchreiben(signal: SpsSignal, request: SpsPruefstatusRequest): Promise<SchreibErgebnis<SpsSignal>> {
    if (!(await this.db.istVerfuegbar())) {
      throw new Error('Ohne Verbindung und ohne lokalen Speicher kann nicht gespeichert werden.');
    }

    const bemerkung = (request.bemerkung ?? '').trim() || null;

    if (request.status === 'NichtOk' && !bemerkung) {
      throw new Error('Bei „Nicht OK“ ist eine Fehlerbeschreibung erforderlich.');
    }

    const db = this.db.db;
    const jetzt = new Date().toISOString();
    const benutzer = this.auth.getAnzeigenameAusToken();

    let ergebnis!: SpsSignal;

    await db.transaction('rw', db.spsSignale, db.syncQueue, async () => {
      const eintrag = await db.spsSignale.get(signal.id);

      if (!eintrag) {
        throw new Error(
          'Dieses Signal steht offline nicht zur Verfügung. '
          + 'Das Projekt muss vorher offline verfügbar gemacht werden.'
        );
      }

      const alterStatus = eintrag.data.pruefstatus;
      const alteBemerkung = eintrag.data.bemerkung ?? null;
      const unveraendert = alterStatus === request.status && (alteBemerkung ?? '') === (bemerkung ?? '');

      if (!unveraendert) {
        eintrag.data.pruefstatus = request.status;
        eintrag.data.bemerkung = bemerkung;
        eintrag.data.zuletztGeprueftAmUtc = jetzt;
        eintrag.data.zuletztGeprueftVon = benutzer;
        eintrag.data.geaendertSeitPruefungAmUtc = null;
        eintrag.data.geaendertSeitPruefungHinweis = null;

        const protokoll: SpsPruefprotokoll & { nichtSynchronisiert: boolean } = {
          id: -Date.now(),
          signalId: signal.id,
          alterStatus,
          neuerStatus: request.status,
          bemerkung,
          geprueftVon: benutzer,
          geprueftAmUtc: jetzt,
          nichtSynchronisiert: true
        };

        eintrag.data.protokolle = [protokoll, ...(eintrag.data.protokolle ?? [])];
        eintrag.data.letzteProtokolle = eintrag.data.protokolle.slice(0, 10);

        await db.spsSignale.put(eintrag);
      }

      ergebnis = eintrag.data;

      /*
       * Gibt es für dasselbe Signal schon eine wartende Änderung, wird sie
       * ERSETZT: der Änderungsstand stammt aus dem Download und wird lokal
       * nicht hochgezählt. Zwei Einträge trügen denselben Stand – der erste
       * ginge durch, der zweite käme als Konflikt zurück.
       */
      const vorhanden = (await db.syncQueue
        .filter(e => e.typ === 'SPS_PRUEFSTATUS' && e.spsSignalId === signal.id && !e.endgueltigFehlgeschlagen)
        .toArray())[0];

      const warteschlange: SyncQueueEintrag = {
        id: vorhanden?.id ?? neueUuid(),
        typ: 'SPS_PRUEFSTATUS',
        entityId: null,
        clientEntityId: null,
        messstellenListeId: 0,
        messstelleId: null,
        projektId: eintrag.projektId,
        spsSignalId: signal.id,
        performedAtUtc: jetzt,
        createdAtUtc: vorhanden?.createdAtUtc ?? jetzt,

        // Der Stand, den der Server noch hat – die Basis der ERSTEN Änderung.
        baseVersion: vorhanden ? (vorhanden.baseVersion ?? null) : (signal.version ?? null),

        // Die Ausgangswerte der ersten Änderung: so erkennt der Server, ob
        // sich am Server dasselbe Feld geändert hat oder nur ein anderes.
        basisWerte: vorhanden?.basisWerte ?? { status: alterStatus, bemerkung: alteBemerkung ?? '' },

        payload: { status: request.status, bemerkung },
        retryCount: 0
      };

      await db.syncQueue.put(warteschlange);
    });

    await this.sync.aktualisiereStatus();

    if (this.network.lohntVersuch()) {
      void this.sync.synchronisiere('aenderung');
    }

    return { daten: ergebnis, lokal: true };
  }

  // ===================================================================
  // LOKALEN BESTAND NACHZIEHEN
  // ===================================================================

  /**
   * Übernimmt Serverstände in lokal vorhandene Signale – aber nie über eine
   * wartende Offline-Änderung hinweg: die ist der einzige Ort, an dem die
   * Prüfung vor Ort existiert.
   */
  private async aktualisiereLokaleSignale(signale: SpsSignal[], auchMitWartend = false): Promise<void> {
    if (signale.length === 0 || !(await this.db.istVerfuegbar())) {
      return;
    }

    const db = this.db.db;

    try {
      await db.transaction('rw', db.spsSignale, db.syncQueue, async () => {
        const wartend = new Set(
          (await db.syncQueue.filter(e => e.typ === 'SPS_PRUEFSTATUS').toArray())
            .map(e => e.spsSignalId)
        );

        for (const server of signale) {
          const lokal = await db.spsSignale.get(server.id);

          if (!lokal || (!auchMitWartend && wartend.has(server.id))) {
            continue;
          }

          this.uebernehmeKopfdaten(lokal, server);
          await db.spsSignale.put(lokal);
        }
      });
    } catch (fehler) {
      console.error(
        'Der lokale SPS-Bestand konnte nicht nachgezogen werden. Die Anzeige stammt vom Server und ist korrekt.',
        fehler
      );
    }
  }

  private uebernehmeKopfdaten(lokal: OfflineSpsSignalEintrag, server: SpsSignal | SpsSignalDetail): void {
    const detail = server as Partial<SpsSignalDetail>;

    lokal.data = {
      ...lokal.data,
      ...server,
      quellen: detail.quellen ?? lokal.data.quellen,
      unterschiede: detail.unterschiede ?? lokal.data.unterschiede,
      letzteProtokolle: detail.letzteProtokolle ?? lokal.data.letzteProtokolle,
      protokolle: lokal.data.protokolle
    };

    // Bestätigte Stände machen lokale Verlaufseinträge überflüssig.
    lokal.data.protokolle = (lokal.data.protokolle ?? []).filter(p => !(p as any).nichtSynchronisiert);

    if (detail.letzteProtokolle) {
      // Online kennt die neuesten zehn – die ergänzen den lokalen Verlauf.
      const bekannt = new Set(lokal.data.protokolle.map(p => p.id));
      const neue = detail.letzteProtokolle.filter(p => !bekannt.has(p.id));
      lokal.data.protokolle = [...neue, ...lokal.data.protokolle]
        .sort((a, b) => b.geprueftAmUtc.localeCompare(a.geprueftAmUtc) || b.id - a.id);
      lokal.data.letzteProtokolle = lokal.data.protokolle.slice(0, 10);
    }
  }

  // ===================================================================
  // HILFSMITTEL
  // ===================================================================

  /**
   * Das gemeinsame Lesemuster: Server, sonst lokal.
   *
   * Der Serverweg ist ein reiner Strom – die Antwort erreicht die Komponente
   * ohne Umweg über den lokalen Speicher. Erst bei einem Netzwerkfehler wird
   * ermittelt, ob das Projekt offline vorliegt; ohne lokalen Bestand wird der
   * Serverfehler durchgereicht, wie bisher.
   */
  private lies<T>(
    server: () => Observable<T>,
    projekt: ProjektQuelle,
    lokal: () => Promise<T>,
    nachziehen?: (daten: T) => Promise<void>
  ): Observable<DatenErgebnis<T>> {
    if (!this.network.lohntVersuch()) {
      return from(this.liesLokal(projekt, lokal));
    }

    return server().pipe(
      this.mitTimeout(SpsDataService.LESE_TIMEOUT_MS),
      tap(daten => {
        this.network.meldeErfolg();

        if (nachziehen) {
          void this.nachziehenWennOffline(projekt, daten, nachziehen);
        }
      }),
      map(daten => ({ daten, quelle: 'server' as const })),
      catchError(fehler => {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          return throwError(() => fehler);
        }

        return from(this.liesLokal(projekt, lokal, fehler));
      })
    );
  }

  private async liesLokal<T>(
    projekt: ProjektQuelle,
    lokal: () => Promise<T>,
    urspruenglicherFehler?: unknown
  ): Promise<DatenErgebnis<T>> {
    const projektId = typeof projekt === 'function' ? await projekt() : projekt;
    const kopf = projektId != null ? await this.db.ladeProjekt(projektId) : undefined;

    if (!kopf) {
      if (urspruenglicherFehler) {
        throw urspruenglicherFehler;
      }

      throw new Error(
        'Keine Verbindung zum Server. Dieses Projekt wurde nicht für die Offline-Nutzung heruntergeladen.'
      );
    }

    return {
      daten: await lokal(),
      quelle: 'offline',
      standUtc: kopf.downloadedAtUtc
    };
  }

  private async nachziehenWennOffline<T>(
    projekt: ProjektQuelle,
    daten: T,
    nachziehen: (daten: T) => Promise<void>
  ): Promise<void> {
    try {
      const projektId = typeof projekt === 'function' ? await projekt() : projekt;

      if (projektId != null && await this.db.ladeProjekt(projektId)) {
        await nachziehen(daten);
      }
    } catch (fehler) {
      console.error('Der lokale SPS-Bestand konnte nicht nachgezogen werden.', fehler);
    }
  }

  private async projektVonListe(listeId: number): Promise<number | null> {
    return (await this.db.ladeSpsListe(listeId))?.projektId ?? null;
  }

  private async projektVonSignal(signalId: number): Promise<number | null> {
    return (await this.db.ladeSpsSignal(signalId))?.projektId ?? null;
  }

  private mitTimeout<T>(ms: number) {
    return timeout<T, Observable<never>>({
      each: ms,
      // Muss ein Observable liefern – ein synchrones throw im Fabrikaufruf
      // wäre ein anderer Fehlerweg und ginge an istNetzwerkfehler vorbei.
      with: () => throwError(() => new TimeoutFehler(ms))
    });
  }

  private behandleFehler(fehler: unknown): void {
    if (NetworkStatusService.istNetzwerkfehler(fehler)) {
      this.network.meldeNetzwerkfehler();
    }
  }
}
